import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { AuthService } from '@services/auth.service';
import { retry, throwError, timer } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SKIP_API_KEY, SKIP_AUTH, SUPPRESS_AUTH_REDIRECTS } from '../http/http-context';

type JwtPayload = Record<string, unknown>;

function decodeJwtPayload(token: string): JwtPayload | null {
	const parts = token.split('.');
	if (parts.length !== 3) return null;

	try {
		const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
		const parsed: unknown = JSON.parse(json);
		return parsed && typeof parsed === 'object' ? (parsed as JwtPayload) : null;
	} catch {
		return null;
	}
}

function isExpired(token: string): boolean {
	const p = decodeJwtPayload(token);
	if (!p || typeof p['exp'] !== 'number') return true;
	const nowSec = Math.floor(Date.now() / 1000);
	return p['exp'] <= nowSec;
}

function isNetworkFailure(err: unknown): boolean {
	if (err instanceof ProgressEvent) return true;
	if (err instanceof HttpErrorResponse) return err.status === 0;
	return false;
}

export const appHttpInterceptor: HttpInterceptorFn = (req, next) => {
	const router = inject(Router);
	const auth = inject(AuthService);

	let headers = req.headers;

	if (!req.context.get(SKIP_AUTH)) {
		const token = auth.getToken?.() ?? null;

		if (token) {
			if (isExpired(token)) {
				auth.clearSession?.();
				if (!req.context.get(SUPPRESS_AUTH_REDIRECTS)) router.navigateByUrl('/auth/login');
				return throwError(
					() =>
						new HttpErrorResponse({
							status: 401,
							statusText: 'Token expired'
						})
				);
			}

			headers = headers.set('Authorization', `Bearer ${token}`);
		}
	}

	if (!req.context.get(SKIP_API_KEY) && environment?.apiKey) {
		headers = headers.set('x-api-key', String(environment.apiKey));
	}

	const cloned = req.clone({ headers });
	const isGet = cloned.method === 'GET';

	let request$ = next(cloned);

	if (isGet) {
		request$ = request$.pipe(
			retry({
				count: 2,
				delay: (err) => {
					if (!isNetworkFailure(err)) {
						return throwError(() => err);
					}
					return timer(400);
				}
			})
		);
	}

	return request$.pipe(
		catchError((err: HttpErrorResponse) => {
			const status = err.status;

			if (status === 401 || status === 403) {
				auth.clearSession?.();
				if (!req.context.get(SUPPRESS_AUTH_REDIRECTS)) router.navigateByUrl('/auth/login');
			} else if (status === 404) {
				if (!req.context.get(SUPPRESS_AUTH_REDIRECTS)) router.navigateByUrl('/auth/login');
			}

			return throwError(() => err);
		})
	);
};
